// src/pages/league-dashboard/TransactionLog.js - NEW FILE
import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, Calendar, Filter, TrendingUp, TrendingDown, Users, DollarSign, Search, ChevronDown, ChevronRight, X } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import { parseIntOrPrev } from '../../services/formUtils';
import { DynastyTable } from '../../services/tableService';
import { useCommissioner } from '../../contexts/CommissionerContext';
import LeagueBanner from '../../components/league-dashboard/LeagueBanner';
import PlayerLink from '../../components/PlayerLink';

// ============================================================
// R.0 STEP 3.G.5 — Module-level pure helpers (named exports for testability).
//
// Per Bible § 14 "Frontend testing convention": helpers and derivers
// are tested by importing the actual function from the component module
// and asserting on its return value. These three pure functions are the
// component's contract surface for partial-failure UI logic.
// ============================================================

/**
 * Derive banner state from reverseTransaction (DELETE
 * /transactions/{id}) response. Reads the airtight rebuild_stats
 * shape (§ 4.5.13.5) and maps the backend outcome onto one of 4
 * canonical UX states (§ 14 "Partial-failure rendering pattern").
 *
 * Per Bible Rule 9: throws on backend contract violation
 * (rebuild_stats=null on a non-skipped response). Pre-3.G.5 this
 * was a silent fallback that hid backend contract drift.
 */
export const deriveBannerFromReversalResponse = (response, transactionId) => {
  const details = response.reversal_details ?? {};
  const cleanupSkipped = details.cleanup_and_rebuild_skipped === true;
  const rebuildStats = details.rebuild_stats;

  if (cleanupSkipped) {
    return { type: 'success_skipped' };
  }
  if (!rebuildStats) {
    throw new Error(
      `Backend contract violation: rebuild_stats is null on ` +
      `non-skipped reverseTransaction response ` +
      `(transaction_id=${transactionId || 'unknown'}). ` +
      `See Bible § 4.5.13.5.`
    );
  }
  const failedTeams = rebuildStats.failed_teams ?? [];
  if (rebuildStats.success && failedTeams.length === 0) {
    return { type: 'success_full' };
  }
  return {
    type: 'success_partial',
    details: { transaction_id: transactionId, rebuild_stats: rebuildStats },
  };
};

/**
 * Derive banner state from rebuildTransactionStats (POST .../rebuild-stats)
 * response. Shape differs slightly: outer success reflects rebuild
 * outcome directly (not preserved-as-true), and the airtight payload
 * lives at rebuild_details (not reversal_details).
 *
 * Per Bible Rule 9: throws on backend contract violation
 * (rebuild_stats=null on a non-skipped response).
 */
export const deriveBannerFromRetryResponse = (response, transactionId) => {
  const details = response.rebuild_details ?? {};
  const cleanupSkipped = details.cleanup_and_rebuild_skipped === true;
  const rebuildStats = details.rebuild_stats;

  if (cleanupSkipped) {
    return { type: 'success_skipped' };
  }
  if (!rebuildStats) {
    throw new Error(
      `Backend contract violation: rebuild_stats is null on ` +
      `non-skipped rebuildTransactionStats response ` +
      `(transaction_id=${transactionId || 'unknown'}). ` +
      `See Bible § 4.5.13.5.`
    );
  }
  const failedTeams = rebuildStats.failed_teams ?? [];
  if (response.success && rebuildStats.success && failedTeams.length === 0) {
    return { type: 'success_full' };
  }
  return {
    type: 'success_partial',
    details: { transaction_id: transactionId, rebuild_stats: rebuildStats },
  };
};

/**
 * Resolve a team_id → display name via the allTeams roster prop.
 * Returns 'Unknown Team' if team not in the provided roster (e.g.,
 * team deleted post-reversal).
 */
export const resolveTeamName = (teamId, allTeams) => {
  const team = (allTeams ?? []).find((t) => t.team_id === teamId);
  return (team && team.team_name) || 'Unknown Team';
};


const TransactionLog = ({ leagueId, leagueStatus, league, leagueBanner, bannerConfig, hideBanner = false, isCommissionerMode: externalCommissionerMode, setIsCommissionerMode: externalSetCommissionerMode, statsLayout = 'grid', allTeams = [] }) => {
  // Check if user is commissioner from league.role
  const isCommissioner = league?.role === 'commissioner';
  
  const [transactions, setTransactions] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [tileFilter, setTileFilter] = useState('total'); // 'total' | 'adds' | 'drops' | 'trades'
  const [tradeStatusFilter, setTradeStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState(30);
  const [expandedTrade, setExpandedTrade] = useState(null);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [stats, setStats] = useState({
    totalTransactions: 0,
    adds: 0,
    drops: 0,
    trades: 0,
    mostActive: null
  });

  // PHASE 3: Commissioner Reversal State
  // Use external state if provided (mobile), otherwise use internal state (desktop)
  const [internalCommissionerMode, setInternalCommissionerMode] = useState(false);
  const isCommissionerMode = externalCommissionerMode !== undefined ? externalCommissionerMode : internalCommissionerMode;
  const setIsCommissionerMode = externalSetCommissionerMode || setInternalCommissionerMode;
  const [reversingTransactionId, setReversingTransactionId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [transactionToReverse, setTransactionToReverse] = useState(null);
  const [reversalError, setReversalError] = useState(null);

  // R.0 STEP 3.G.5: post-reversal banner state. Replaces the previous
  // boolean reversalSuccess flag with an enum-shaped state covering
  // 4 canonical UX states (per Bible § 14 Partial-failure rendering
  // pattern):
  //   { type: 'success_full' }      → green, "stats rebuilt"
  //   { type: 'success_skipped' }   → green, "rebuild not needed"
  //   { type: 'success_partial', details: { transaction_id, rebuild_stats } }
  //                                  → yellow, "N team failures" + Show details
  //   { type: 'error', message }     → red, error message
  const [banner, setBanner] = useState(null);
  const [retryingTransactionId, setRetryingTransactionId] = useState(null);
  const [showPartialFailureModal, setShowPartialFailureModal] = useState(false);
  // Retry error rendered INSIDE the partial-failure modal (most contextual
  // surface per § 14 Amendment A asymmetry codification). Top-level
  // banner.type === 'error' is reserved for the no-modal-open case
  // (currently unreachable from production handlers; kept for future
  // monitoring-triggered retries that lack a UI surface).
  const [retryError, setRetryError] = useState(null);

  // Always fetch ALL transactions (no server-side type filter) so tile counts stay correct
  useEffect(() => {
    loadTransactions();
    loadTrades();
  }, [leagueId, dateRange, tradeStatusFilter]);

  // Recalculate stats whenever transactions or trades change
  useEffect(() => {
    calculateStats(transactions, trades);
  }, [transactions, trades]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const response = await leaguesAPI.getTransactions(leagueId, {
        days_back: dateRange,
        limit: 5000
      });

      if (response.success) {
        setTransactions(response.transactions ?? []);
      }
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrades = async () => {
    try {
      const response = await leaguesAPI.getTradeHistory(leagueId, {
        status: tradeStatusFilter === 'all' ? null : tradeStatusFilter,
        limit: 100
      });

      if (response.success) {
        // FILTER OUT PROPOSED TRADES - only show completed/executed trades in transaction log
        const completedTrades = (response.trades ?? []).filter(t =>
          t.status === 'completed' || t.status === 'executed'
        );
        setTrades(completedTrades);
        // Stats will be calculated by the useEffect that watches transactions and trades
      }
    } catch (error) {
      console.error('Error loading trades:', error);
    }
  };

  const ADD_TYPES = ['free_agent', 'fa_acquisition', 'commissioner_add', 'draft_pick', 'minor_draft_pick', 'autodraft', 'minor_promotion', 'keeper_selection'];
  const DROP_TYPES = ['release', 'offseason_reset'];

  const calculateStats = (txns, trds = []) => {
    const completedTrades = trds.filter(t => t.status === 'completed' || t.status === 'executed');

    const stats = {
      totalTransactions: txns.length + completedTrades.length,
      adds: txns.filter(t => ADD_TYPES.includes(t.transaction_type)).length,
      drops: txns.filter(t => DROP_TYPES.includes(t.transaction_type)).length,
      trades: completedTrades.length,
      mostActive: null
    };

    // Find most active team
    const teamCounts = {};
    txns.forEach(t => {
      const team = t.to_team_name || t.from_team_name;
      if (team && team !== 'Free Agency') {
        teamCounts[team] = (teamCounts[team] || 0) + 1;
      }
    });
    
    // Add trades to team counts
    completedTrades.forEach(t => {
      if (t.proposing_team?.team_name) {
        teamCounts[t.proposing_team?.team_name] = (teamCounts[t.proposing_team?.team_name] || 0) + 1;
      }
      if (t.receiving_team?.team_name) {
        teamCounts[t.receiving_team?.team_name] = (teamCounts[t.receiving_team?.team_name] || 0) + 1;
      }
    });

    const mostActiveTeam = Object.entries(teamCounts)
      .sort(([,a], [,b]) => b - a)[0];
    
    if (mostActiveTeam) {
      stats.mostActive = { team: mostActiveTeam[0], count: mostActiveTeam[1] };
    }

    setStats(stats);
  };

  // PHASE 3: Transaction Reversal Handlers
  const handleUndoClick = (transaction) => {
    setTransactionToReverse(transaction);
    setShowConfirmModal(true);
    setReversalError(null);
  };

  // R.0 STEP 3.G.5 derivers + resolveTeamName are module-level named
  // exports defined at top of file. Component-internal references
  // call them directly (see top-of-file definitions).

  const handleConfirmReversal = async () => {
    if (!transactionToReverse) return;

    const transactionId = transactionToReverse.transaction_id;
    try {
      setReversingTransactionId(transactionId);
      setReversalError(null);

      const response = await leaguesAPI.reverseTransaction(
        leagueId,
        transactionId
      );

      if (response.success) {
        setShowConfirmModal(false);

        // Refresh transaction lists after reversal
        await loadTransactions();
        await loadTrades();

        // Derive banner state from response (Rule 12: single derivation site).
        const newBanner = deriveBannerFromReversalResponse(response, transactionId);
        setBanner(newBanner);

        // Auto-dismiss for non-partial-failure success states (3s).
        // Partial failure stays visible until user dismisses or retries.
        if (newBanner.type === 'success_full' || newBanner.type === 'success_skipped') {
          setTimeout(() => setBanner(null), 3000);
        }
      } else {
        setReversalError(response.message || 'Failed to reverse transaction');
      }
    } catch (error) {
      console.error('Error reversing transaction:', error);
      setReversalError(error.message || 'An error occurred while reversing the transaction');
    } finally {
      setReversingTransactionId(null);
      setTransactionToReverse(null);
    }
  };

  const handleCancelReversal = () => {
    setShowConfirmModal(false);
    setTransactionToReverse(null);
    setReversalError(null);
  };

  // R.0 STEP 3.G.5: retry stats rebuild for a partial-failure transaction.
  // Calls the 3.E endpoint which preserves § 4.5.13.7 single-source-ID
  // contract (only transaction_id passed; service re-derives all scope).
  const handleRetryRebuild = async () => {
    if (!banner || banner.type !== 'success_partial') return;
    const transactionId = banner.details.transaction_id;
    if (!transactionId) return;

    try {
      setRetryingTransactionId(transactionId);
      setRetryError(null);  // clear any prior in-modal retry error
      const response = await leaguesAPI.rebuildTransactionStats(
        leagueId,
        transactionId
      );

      // Refresh after retry to pick up any state changes.
      await loadTransactions();
      await loadTrades();

      const newBanner = deriveBannerFromRetryResponse(response, transactionId);
      setBanner(newBanner);

      // Auto-dismiss + close modal on full success or skipped.
      if (newBanner.type === 'success_full' || newBanner.type === 'success_skipped') {
        setShowPartialFailureModal(false);
        setTimeout(() => setBanner(null), 3000);
      }
      // If still partial: modal stays open with updated details so user can retry again.
    } catch (error) {
      console.error('Error retrying rebuild:', error);
      // Per § 14 Amendment A: render error at the most contextual surface
      // available. Modal is open here → inline retry error inside modal.
      // Top-level banner.type === 'error' is reserved for the no-modal-open
      // case (e.g., monitoring-triggered retries with no UI surface).
      setRetryError(error.message || 'Failed to retry stats rebuild');
    } finally {
      setRetryingTransactionId(null);
    }
  };

  const handleClosePartialFailureModal = () => {
    setShowPartialFailureModal(false);
    setRetryError(null);
  };

  // resolveTeamName is a module-level named export defined at
  // top of file. Component-internal references call it directly
  // with `allTeams` prop passed as argument.

  // Client-side filtering: dropdown + tile + search all work together
  const filteredTransactions = transactions.filter(t => {
    // Dropdown filter: specific transaction type
    if (filter !== 'all' && filter !== 'trade' && t.transaction_type !== filter) return false;
    // If dropdown is set to 'trade', hide non-trade transactions
    if (filter === 'trade') return false;

    // Tile filter: category-level
    if (tileFilter === 'adds' && !ADD_TYPES.includes(t.transaction_type)) return false;
    if (tileFilter === 'drops' && !DROP_TYPES.includes(t.transaction_type)) return false;
    if (tileFilter === 'trades') return false;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (t.player_name || '').toLowerCase().includes(term) ||
             (t.from_team_name || '').toLowerCase().includes(term) ||
             (t.to_team_name || '').toLowerCase().includes(term);
    }
    return true;
  });

  const filteredTrades = trades.filter(t => {
    // Dropdown filter: if a specific non-trade type is selected, hide trades
    if (filter !== 'all' && filter !== 'trade') return false;
    // Tile filter
    if (tileFilter === 'adds' || tileFilter === 'drops') return false;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (t.proposing_team?.team_name || '').toLowerCase().includes(term) ||
             (t.receiving_team?.team_name || '').toLowerCase().includes(term) ||
             (t.notes || '').toLowerCase().includes(term) ||
             JSON.stringify(t.proposing_team_gave).toLowerCase().includes(term) ||
             JSON.stringify(t.receiving_team_gave).toLowerCase().includes(term);
    }
    return true;
  });

  // Show sections based on active filters
  const shouldShowTransactions = filter !== 'trade' && tileFilter !== 'trades';
  const shouldShowTrades = (filter === 'all' || filter === 'trade') && tileFilter !== 'adds' && tileFilter !== 'drops';

  const handleTileClick = (tile) => {
    setTileFilter(prev => (prev === tile && tile !== 'total') ? 'total' : tile);
    // Reset dropdown to 'All Types' when using tiles, unless clicking Total
    if (tile !== 'total') setFilter('all');
  };

  const columns = [
    {
      key: 'transaction_date',
      title: 'Date',
      width: 120,
      render: (value, transaction) => (
        <div className="space-y-1">
          <div className={transaction?.is_reversed ? 'line-through text-gray-500' : ''}>
            {new Date(value).toLocaleDateString()}
          </div>
          {transaction?.is_reversed && (
            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-500/20 text-gray-400">
              Reversed
            </span>
          )}
        </div>
      )
    },
    {
      key: 'transaction_type',
      title: 'Type',
      width: 80,
      render: (value, transaction) => {
        const badges = {
          free_agent_claim: { color: 'bg-green-500/20 text-green-400', icon: '➕' },
          fa_acquisition: { color: 'bg-green-500/20 text-green-400', icon: '➕' },
          fa_add: { color: 'bg-green-500/20 text-green-400', icon: '➕' },
          release: { color: 'bg-red-500/20 text-red-400', icon: '➖' },
          roster_move: { color: 'bg-purple-500/20 text-purple-400', icon: '🔀' },
          trade: { color: 'bg-blue-500/20 text-blue-400', icon: '🔄' },
          commissioner_add: { color: 'bg-yellow-500/20 text-yellow-400', icon: '⭐' },
          autodraft: { color: 'bg-orange-500/20 text-orange-400', icon: '🤖' },
          minor_draft_pick: { color: 'bg-pink-500/20 text-pink-400', icon: '🎯' },
          draft_pick: { color: 'bg-pink-500/20 text-pink-400', icon: '🎯' },
          draft_pick_reversal: { color: 'bg-gray-500/20 text-gray-400', icon: '↩️' },
          minor_promotion: { color: 'bg-cyan-500/20 text-cyan-400', icon: '⬆️' },
          keeper_selection: { color: 'bg-indigo-500/20 text-indigo-400', icon: '🔒' },
          offseason_reset: { color: 'bg-gray-500/20 text-gray-400', icon: '🔄' },
        };
        const badge = badges[value] || { color: 'bg-gray-500/20', icon: '•' };
        // A waiver award is written as fa_acquisition with details.acquisition_method
        // 'waiver_claim' (Bible 22, allowed type/method disagreement). Relabel it
        // "Waiver" while keeping the add styling/icon. No fallback: absent
        // acquisition_method renders as the normal FA add, which is correct.
        const isWaiver = value === 'fa_acquisition' && transaction?.acquisition_method === 'waiver_claim';
        const label = isWaiver ? 'Waiver' : value;
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
            {badge.icon} {label}
          </span>
        );
      }
    },
    {
      key: 'player_name',
      title: 'Player',
      width: 140
    },
    {
      key: 'position',
      title: 'Pos',
      width: 60
    },
    {
      key: 'from_team_name',
      title: 'From',
      width: 120,
      textAlign: 'text-left',
      render: (value) => value || 'Free Agency'
    },
    {
      key: 'to_team_name',
      title: 'To',
      width: 120,
      textAlign: 'text-left',
      render: (value) => value || 'Free Agency'
    },
    {
      key: 'salary',
      title: 'Salary',
      width: 60,
      render: (value, transaction) => {
        // Release salary is intentionally NOT shown: a released player's salary
        // is spent (sunk), not returned to the team, so rendering "$N" here would
        // misread as a refund. Empty cell on release.
        if (transaction?.transaction_type === 'release') return '';
        return value ? `$${value}` : '-';
      }
    },
    {
      key: 'contract_years',
      title: 'Contract',
      width: 70,
      // contract_years is already present in the row payload (serialize layer).
      // Literal default only when absent (empty cell), no fallback to another field.
      render: (value) => value ? `${value} yr` : ''
    },
    {
      key: 'notes',
      title: 'Notes',
      width: 160,
      textAlign: 'text-left',
      render: (value) => value || '-'
    }
  ];

  // PHASE 3: Add Actions column when commissioner mode is active
  if (isCommissionerMode) {
    columns.push({
      key: 'actions',
      title: 'Actions',
      width: 120,
      render: (value, transaction) => (
        <button
          onClick={() => handleUndoClick(transaction)}
          disabled={reversingTransactionId === transaction.transaction_id}
          className="px-3 py-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs rounded transition-colors"
        >
          {reversingTransactionId === transaction.transaction_id ? 'Reversing...' : '↩️ Undo'}
        </button>
      )
    });
  }

  return (
    <div className="space-y-6">
      {/* Banner Header */}
      {!hideBanner && (
      <LeagueBanner
        leagueBanner={leagueBanner}
        title="League Transactions"
        subtitle="Track all player movements and roster changes"
        minHeight="160px"
        bannerConfig={bannerConfig}
        actions={
          isCommissioner && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-400">Commissioner Mode</span>
              <button
                onClick={() => setIsCommissionerMode(!isCommissionerMode)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isCommissionerMode ? 'bg-yellow-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isCommissionerMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )
        }
      />
      )}

      {/* R.0 STEP 3.G.5: Reversal banner — 4 canonical UX states.
          Banner state derived once in handleConfirmReversal /
          handleRetryRebuild (Rule 12). */}
      {banner?.type === 'success_full' && (
        <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
          <p className="text-green-400 font-medium">
            ✅ Transaction reversed successfully! Stats have been rebuilt.
          </p>
        </div>
      )}

      {banner?.type === 'success_skipped' && (
        <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
          <p className="text-green-400 font-medium">
            ✅ Transaction reversed. No historical stats data; rebuild not needed.
          </p>
        </div>
      )}

      {banner?.type === 'success_partial' && (
        <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-4 flex justify-between items-center">
          <p className="text-yellow-400 font-medium">
            ⚠️ Transaction reversed, but stats rebuild had{' '}
            {(banner.details?.rebuild_stats?.failed_teams ?? []).length} per-team failure(s).
          </p>
          <button
            onClick={() => setShowPartialFailureModal(true)}
            className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-sm rounded ml-3"
          >
            Show details
          </button>
        </div>
      )}

      {banner?.type === 'error' && (
        <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
          <p className="text-red-400 font-medium">
            ❌ Error: {banner.message}
          </p>
        </div>
      )}

      {/* Stats Cards */}
      {statsLayout === 'grid' ? (
        /* Desktop: Grid Layout */
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className={dynastyTheme.components.card.base} onClick={() => handleTileClick('total')} style={{ cursor: 'pointer', border: tileFilter === 'total' ? '1.5px solid #9ca3af' : undefined }}>
            <div className="p-4">
              <div className="text-sm text-gray-400">Total</div>
              <div className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>{stats.totalTransactions}</div>
            </div>
          </div>
          <div className={dynastyTheme.components.card.base} onClick={() => handleTileClick('adds')} style={{ cursor: 'pointer', border: tileFilter === 'adds' ? '1.5px solid #4ade80' : undefined }}>
            <div className="p-4">
              <div className="text-sm text-green-400">Adds</div>
              <div className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>{stats.adds}</div>
            </div>
          </div>
          <div className={dynastyTheme.components.card.base} onClick={() => handleTileClick('drops')} style={{ cursor: 'pointer', border: tileFilter === 'drops' ? '1.5px solid #f87171' : undefined }}>
            <div className="p-4">
              <div className="text-sm text-red-400">Drops</div>
              <div className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>{stats.drops}</div>
            </div>
          </div>
          <div className={dynastyTheme.components.card.base} onClick={() => handleTileClick('trades')} style={{ cursor: 'pointer', border: tileFilter === 'trades' ? '1.5px solid #60a5fa' : undefined }}>
            <div className="p-4">
              <div className="text-sm text-blue-400">Trades</div>
              <div className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>{stats.trades}</div>
            </div>
          </div>
          <div className={dynastyTheme.components.card.base}>
            <div className="p-4">
              <div className="text-sm text-yellow-400">Most Active</div>
              <div className={`text-lg font-bold truncate ${dynastyTheme.classes.text.white}`}>
                {stats.mostActive ? `${stats.mostActive.team} (${stats.mostActive.count})` : '-'}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Mobile: Horizontal Layout - COMPACT */
        <div className={dynastyTheme.components.card.base}>
          <div className="p-2">
            <div className="flex items-center justify-between gap-2 text-xs">
              <div className="flex-1 text-center">
                <div className="text-gray-400 mb-0.5 text-[10px]">Total</div>
                <div className={`text-base font-bold ${dynastyTheme.classes.text.white}`}>{stats.totalTransactions}</div>
              </div>
              <div className="w-px h-6 bg-gray-700"></div>
              <div className="flex-1 text-center">
                <div className="text-green-400 mb-0.5 text-[10px]">Adds</div>
                <div className={`text-base font-bold ${dynastyTheme.classes.text.white}`}>{stats.adds}</div>
              </div>
              <div className="w-px h-6 bg-gray-700"></div>
              <div className="flex-1 text-center">
                <div className="text-red-400 mb-0.5 text-[10px]">Drops</div>
                <div className={`text-base font-bold ${dynastyTheme.classes.text.white}`}>{stats.drops}</div>
              </div>
              <div className="w-px h-6 bg-gray-700"></div>
              <div className="flex-1 text-center">
                <div className="text-blue-400 mb-0.5 text-[10px]">Trades</div>
                <div className={`text-base font-bold ${dynastyTheme.classes.text.white}`}>{stats.trades}</div>
              </div>
            </div>
            {stats.mostActive && (
              <div className="mt-2 pt-2 border-t border-gray-700 text-center">
                <span className="text-[10px] text-yellow-400">Most Active: </span>
                <span className={`text-xs font-bold ${dynastyTheme.classes.text.white}`}>
                  {stats.mostActive.team} ({stats.mostActive.count})
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters - COMPACT */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-2 space-y-2">
          {/* Row 1: Dropdowns */}
          <div className="flex gap-2">
            {/* Type Filter */}
            <select
              value={filter}
              onChange={(e) => {
                const val = e.target.value;
                setFilter(val);
                // Auto-highlight the matching tile
                if (val === 'all') setTileFilter('total');
                else if (val === 'trade') setTileFilter('trades');
                else if (ADD_TYPES.includes(val)) setTileFilter('adds');
                else if (DROP_TYPES.includes(val)) setTileFilter('drops');
                else setTileFilter('total');
              }}
              className={`${dynastyTheme.components.input} flex-1 text-xs py-1`}
            >
              <option value="all">All Types</option>
              <optgroup label="Adds">
                <option value="fa_acquisition">FA Acquisition</option>
                <option value="free_agent_claim">FA Claim (Waivers)</option>
                <option value="commissioner_add">Commissioner Add</option>
                <option value="draft_pick">Draft Pick</option>
                <option value="minor_draft_pick">ML Draft Pick</option>
                <option value="autodraft">Auto Draft</option>
                <option value="minor_promotion">Minor Promotion</option>
                <option value="keeper_selection">Keeper Selection</option>
              </optgroup>
              <optgroup label="Drops">
                <option value="release">Release</option>
                <option value="offseason_reset">Offseason Reset</option>
              </optgroup>
              <optgroup label="Trades">
                <option value="trade">Trade</option>
              </optgroup>
              <optgroup label="Other">
                <option value="roster_move">Roster Move</option>
                <option value="draft_pick_reversal">Pick Reversal</option>
              </optgroup>
            </select>

            {/* Date Range */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(parseIntOrPrev(e.target.value, dateRange))}
              className={`${dynastyTheme.components.input} flex-1 text-xs py-1`}
            >
              <option value={7}>Last 7 Days</option>
              <option value={14}>Last 14 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 3 Months</option>
              <option value={365}>All Time</option>
            </select>

            {/* Trade Status Filter (only show when viewing trades) */}
            {(filter === 'all' || filter === 'trade') && (
              <select
                value={tradeStatusFilter}
                onChange={(e) => setTradeStatusFilter(e.target.value)}
                className={`${dynastyTheme.components.input} flex-1 text-xs py-1`}
              >
                <option value="all">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="executed">Executed</option>
              </select>
            )}
          </div>

          {/* Row 2: Search */}
          <div className="relative">
            <Search className="absolute left-2 top-1.5 w-3 h-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search players, teams..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${dynastyTheme.components.input} w-full pl-7 text-xs py-1`}
            />
          </div>
        </div>
      </div>

      {/* Trades Section */}
      {shouldShowTrades && filteredTrades.length > 0 && (
        <div className={dynastyTheme.components.card.base}>
          <div className="px-4 py-2 border-b border-gray-700">
            <h2 className={`text-base font-bold ${dynastyTheme.classes.text.white} flex items-center gap-2`}>
              <ArrowRightLeft className="w-4 h-4" />
              Trades
            </h2>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {filteredTrades.map(trade => (
                <TradeRow
                  key={trade.trade_id}
                  trade={trade}
                  expanded={expandedTrade === trade.trade_id}
                  onToggle={() => setExpandedTrade(expandedTrade === trade.trade_id ? null : trade.trade_id)}
                  onViewDetails={() => setSelectedTrade(trade)}
                  isCommissionerMode={isCommissionerMode}
                  onUndoClick={handleUndoClick}
                  reversingTransactionId={reversingTransactionId}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Transactions Table */}
      {shouldShowTransactions && (
        <div className={dynastyTheme.components.card.base}>
          <div className="px-4 py-2 border-b border-gray-700">
            <h2 className={`text-base font-bold ${dynastyTheme.classes.text.white}`}>
              Roster Moves
            </h2>
          </div>
          <div className="p-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto"></div>
                <p className="mt-4">Loading transactions...</p>
              </div>
            ) : filteredTransactions.length > 0 ? (
              <DynastyTable
                data={filteredTransactions}
                columns={columns}
                tableType="flat-data"
                initialSort={{ key: 'transaction_date', direction: 'desc' }}
                maxHeight="600px"
              />
            ) : (
              <div className="text-center py-12">
                <ArrowRightLeft className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                <p className="text-gray-400">No transactions found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Trade Detail Modal */}
      {selectedTrade && (
        <TradeDetailModal
          trade={selectedTrade}
          onClose={() => setSelectedTrade(null)}
        />
      )}

      {/* PHASE 3: Transaction Reversal Confirmation Modal */}
      {showConfirmModal && transactionToReverse && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className={`${dynastyTheme.classes.bg.darkCard} rounded-lg max-w-2xl w-full`}>
            {/* Header */}
            <div className={`p-6 border-b ${dynastyTheme.classes.border.neutral}`}>
              <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
                ⚠️ Reverse Transaction?
              </h2>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <p className={dynastyTheme.classes.text.white}>
                Are you sure you want to reverse this transaction?
              </p>

              {/* Transaction Details */}
              <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.neutral}`}>
                <p className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                  Transaction Details:
                </p>
                <p className={`font-medium ${dynastyTheme.classes.text.white}`}>
                  {transactionToReverse.transaction_type === 'trade' ? (
                    `Trade: ${transactionToReverse.notes || 'Trade transaction'}`
                  ) : (
                    `${transactionToReverse.transaction_type.toUpperCase()}: ${transactionToReverse.player_name || 'Unknown Player'}`
                  )}
                </p>
                <p className={`text-sm ${dynastyTheme.classes.text.neutralLight} mt-1`}>
                  Date: {new Date(transactionToReverse.transaction_date).toLocaleDateString()}
                </p>
              </div>

              {/* Warning Box */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-400 font-medium mb-2">
                  This action will:
                </p>
                <ul className="text-sm text-yellow-300 space-y-1 ml-4">
                  <li>• Mark the transaction as reversed</li>
                  <li>• Restore rosters to their previous state</li>
                  <li>• Rebuild all stats from this date forward</li>
                  <li>• Take approximately 30-60 seconds to complete</li>
                </ul>
              </div>

              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-400 font-bold">
                  ⚠️ This action CANNOT be undone!
                </p>
              </div>

              {/* Error Display */}
              {reversalError && (
                <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
                  <p className="text-red-400 font-medium">
                    Error: {reversalError}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`p-6 border-t ${dynastyTheme.classes.border.neutral} flex justify-end gap-3`}>
              <button
                onClick={handleCancelReversal}
                disabled={reversingTransactionId !== null}
                className={`${dynastyTheme.components.button.secondary} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReversal}
                disabled={reversingTransactionId !== null}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded transition-colors"
              >
                {reversingTransactionId !== null ? 'Reversing...' : 'Confirm Reversal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* R.0 STEP 3.G.5: Partial-failure detail modal.
          Opens via "Show details" CTA on the success_partial banner.
          Lists failed team names (resolved via allTeams prop) with
          per-team failure context. Footer offers Retry Rebuild CTA
          which calls the 3.E endpoint. */}
      {showPartialFailureModal && banner?.type === 'success_partial' && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className={`${dynastyTheme.classes.bg.darkCard} rounded-lg max-w-2xl w-full`}>
            {/* Header */}
            <div className={`p-6 border-b ${dynastyTheme.classes.border.neutral}`}>
              <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
                Stats Rebuild Status
              </h2>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <p className={dynastyTheme.classes.text.white}>
                Transaction was reversed, but the following teams' stats
                rebuild had failures:
              </p>
              <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.neutral}`}>
                <ul className="space-y-2">
                  {(banner.details?.rebuild_stats?.failed_teams ?? []).map((entry, idx) => (
                    <li key={`${entry.team_id}-${entry.date || idx}`} className="text-sm">
                      <span className={`font-medium ${dynastyTheme.classes.text.white}`}>
                        {resolveTeamName(entry.team_id, allTeams)}
                      </span>
                      <span className={`${dynastyTheme.classes.text.neutralLight} ml-2`}>
                        — {entry.operation || 'rebuild'} on {entry.date || 'unknown date'}:{' '}
                        {entry.error_message || 'rebuild failed'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-400 text-sm">
                  Click "Retry Rebuild" to re-run stats rebuild for this transaction.
                  The retry is idempotent — it will re-derive affected teams + players
                  from the transaction record.
                </p>
              </div>

              {/* Inline retry error (most contextual surface per
                  § 14 Amendment A) */}
              {retryError && (
                <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
                  <p className="text-red-400 font-medium">
                    ❌ Retry failed: {retryError}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`p-6 border-t ${dynastyTheme.classes.border.neutral} flex justify-end gap-3`}>
              <button
                onClick={handleClosePartialFailureModal}
                disabled={retryingTransactionId !== null}
                className={`${dynastyTheme.components.button.secondary} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Close
              </button>
              <button
                onClick={handleRetryRebuild}
                disabled={retryingTransactionId !== null}
                className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded transition-colors"
              >
                {retryingTransactionId !== null ? 'Retrying...' : 'Retry Rebuild'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// TradeRow Component
const TradeRow = ({ trade, expanded, onToggle, onViewDetails, isCommissionerMode, onUndoClick, reversingTransactionId }) => {
  const getStatusBadge = (status) => {
    const badges = {
      completed: { color: 'bg-green-500/20 text-green-400', label: 'Completed' },
      executed: { color: 'bg-green-500/20 text-green-400', label: 'Executed' },
      proposed: { color: 'bg-yellow-500/20 text-yellow-400', label: 'Proposed' },
      accepted: { color: 'bg-blue-500/20 text-blue-400', label: 'Accepted' },
      rejected: { color: 'bg-red-500/20 text-red-400', label: 'Rejected' },
      cancelled: { color: 'bg-gray-500/20 text-gray-400', label: 'Cancelled' },
      vetoed: { color: 'bg-red-500/20 text-red-400', label: 'Vetoed' }
    };
    const badge = badges[status] || { color: 'bg-gray-500/20', label: status };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const summarizeAssets = (teamGives) => {
    const players = teamGives?.players || [];
    const picks = teamGives?.picks || [];
    const cash = teamGives?.cash || 0;
    const parts = [];
    if (players.length > 0) {
      parts.push(players.map(p => p.info?.full_name).join(', '));
    }
    if (picks.length > 0) {
      parts.push(picks.map(p => `${p.pick_type === 'ml' ? 'ML ' : ''}Rd ${p.round} Pick`).join(', '));
    }
    if (cash > 0) {
      parts.push(`$${cash}`);
    }
    return parts.join(' + ') || 'Nothing';
  };

  const proposingSummary = summarizeAssets(trade.proposing_team_gave);
  const receivingSummary = summarizeAssets(trade.receiving_team_gave);

  return (
    <div className={`${dynastyTheme.classes.bg.darkLighter} rounded-lg border ${dynastyTheme.classes.border.neutral} overflow-hidden`}>
      {/* Trade Header */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <button
              onClick={onToggle}
              className={`p-1 hover:bg-gray-700 rounded ${dynastyTheme.classes.transition}`}
            >
              {expanded ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className={`font-bold ${dynastyTheme.classes.text.white}`}>
                  {trade.proposing_team?.team_name}
                </span>
                <ArrowRightLeft className="w-4 h-4 text-teal-400" />
                <span className={`font-bold ${dynastyTheme.classes.text.white}`}>
                  {trade.receiving_team?.team_name}
                </span>
                {getStatusBadge(trade.status)}
              </div>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                {formatDate(trade.proposed_at)}
                {trade.executed_at && ` • Executed ${formatDate(trade.executed_at)}`}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            {isCommissionerMode && (
              <button
                onClick={() => onUndoClick(trade)}
                disabled={reversingTransactionId === trade.trade_id}
                className="px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm rounded transition-colors"
              >
                {reversingTransactionId === trade.trade_id ? 'Reversing...' : '↩️ Undo'}
              </button>
            )}
            <button
              onClick={onViewDetails}
              className={`px-4 py-2 ${dynastyTheme.components.button.primary} text-sm`}
            >
              View Details
            </button>
          </div>
        </div>

        {/* Quick Summary */}
        {!expanded && (
          <div className="mt-3 flex gap-4 text-sm">
            <div>
              <span className={dynastyTheme.classes.text.neutralLight}>Traded: </span>
              <span className={dynastyTheme.classes.text.white}>
                {proposingSummary}
              </span>
            </div>
            <div>
              <span className={dynastyTheme.classes.text.neutralLight}>For: </span>
              <span className={dynastyTheme.classes.text.white}>
                {receivingSummary}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className={`border-t ${dynastyTheme.classes.border.neutral} p-4 ${dynastyTheme.classes.bg.dark}`}>
          <div className="grid grid-cols-2 gap-6">
            {/* Proposing Team Gives */}
            <div>
              <h4 className={`text-sm font-bold ${dynastyTheme.classes.text.primary} mb-2`}>
                {trade.proposing_team?.team_name} Gives:
              </h4>
              <TradeAssets assets={trade.proposing_team_gave} />
            </div>

            {/* Receiving Team Gives */}
            <div>
              <h4 className={`text-sm font-bold ${dynastyTheme.classes.text.primary} mb-2`}>
                {trade.receiving_team?.team_name} Gives:
              </h4>
              <TradeAssets assets={trade.receiving_team_gave} />
            </div>
          </div>

          {/* Notes */}
          {trade.notes && (
            <div className="mt-4 pt-4 border-t ${dynastyTheme.classes.border.neutral}">
              <p className={`text-sm ${dynastyTheme.classes.text.neutralLight} italic`}>
                "{trade.notes}"
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// TradeAssets Component
const TradeAssets = ({ assets }) => {
  if (!assets) return <p className="text-sm text-gray-500">Nothing</p>;

  const { players = [], picks = [], cash = 0 } = assets;

  return (
    <div className="space-y-2">
      {/* Players */}
      {players.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-1">Players:</p>
          {players.map((player, idx) => (
            <div key={idx} className={`text-sm ${dynastyTheme.classes.text.white}`}>
              • <PlayerLink mlbId={player.ids?.mlb} name={player.info?.full_name} style={{ color: '#f9fafb' }} />
              {player.info?.position && ` (${player.info.position})`}
            </div>
          ))}
        </div>
      )}

      {/* Draft Picks */}
      {picks.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-1">Draft Picks:</p>
          {picks.map((pick, idx) => (
            <div key={idx} className={`text-sm ${dynastyTheme.classes.text.white}`}>
              • {pick.year} {pick.pick_type === 'ml' ? 'ML ' : ''}Round {pick.round}
              {pick.original_team && ` (${pick.original_team})`}
            </div>
          ))}
        </div>
      )}

      {/* Cash */}
      {cash > 0 && (
        <div>
          <p className="text-xs text-gray-500 mb-1">Cash:</p>
          <div className={`text-sm ${dynastyTheme.classes.text.white}`}>
            ${cash}
          </div>
        </div>
      )}

      {/* Empty state */}
      {players.length === 0 && picks.length === 0 && cash === 0 && (
        <p className="text-sm text-gray-500">Nothing</p>
      )}
    </div>
  );
};

// TradeDetailModal Component
const TradeDetailModal = ({ trade, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className={`${dynastyTheme.classes.bg.darkCard} rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
        {/* Header */}
        <div className={`p-6 border-b ${dynastyTheme.classes.border.neutral} flex items-center justify-between`}>
          <h2 className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
            Trade Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded transition-colors"
          >
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Trade Summary */}
          <div className="flex items-center justify-center gap-6">
            <div className="text-center">
              <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.primary}`}>
                {trade.proposing_team?.team_name}
              </h3>
              <p className="text-sm text-gray-500">Proposing Team</p>
            </div>
            <ArrowRightLeft className="w-8 h-8 text-teal-400" />
            <div className="text-center">
              <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.primary}`}>
                {trade.receiving_team?.team_name}
              </h3>
              <p className="text-sm text-gray-500">Receiving Team</p>
            </div>
          </div>

          {/* Trade Assets */}
          <div className="grid grid-cols-2 gap-6">
            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.neutral}`}>
              <h4 className={`font-bold ${dynastyTheme.classes.text.white} mb-3`}>
                {trade.proposing_team?.team_name} Gives:
              </h4>
              <TradeAssets assets={trade.proposing_team_gave} />
            </div>

            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter} border ${dynastyTheme.classes.border.neutral}`}>
              <h4 className={`font-bold ${dynastyTheme.classes.text.white} mb-3`}>
                {trade.receiving_team?.team_name} Gives:
              </h4>
              <TradeAssets assets={trade.receiving_team_gave} />
            </div>
          </div>

          {/* Timeline */}
          <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
            <h4 className={`font-bold ${dynastyTheme.classes.text.white} mb-3`}>Timeline</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Proposed:</span>
                <span className={dynastyTheme.classes.text.white}>
                  {new Date(trade.proposed_at).toLocaleString()}
                </span>
              </div>
              {trade.responded_at && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Responded:</span>
                  <span className={dynastyTheme.classes.text.white}>
                    {new Date(trade.responded_at).toLocaleString()}
                  </span>
                </div>
              )}
              {trade.executed_at && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Executed:</span>
                  <span className={dynastyTheme.classes.text.white}>
                    {new Date(trade.executed_at).toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-400">Status:</span>
                <span className={dynastyTheme.classes.text.white}>
                  {trade.status}
                </span>
              </div>
            </div>
          </div>

          {/* Notes & Rejection Reason */}
          {trade.notes && (
            <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.darkLighter}`}>
              <h4 className={`font-bold ${dynastyTheme.classes.text.white} mb-2`}>Notes</h4>
              <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                {trade.notes}
              </p>
            </div>
          )}

          {trade.rejection_reason && (
            <div className={`p-4 rounded-lg bg-red-500/10 border border-red-500/30`}>
              <h4 className="font-bold text-red-400 mb-2">Rejection Reason</h4>
              <p className="text-sm text-red-300">
                {trade.rejection_reason}
              </p>
            </div>
          )}

          {trade.commissioner_notes && (
            <div className={`p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30`}>
              <h4 className="font-bold text-yellow-400 mb-2">Commissioner Notes</h4>
              <p className="text-sm text-yellow-300">
                {trade.commissioner_notes}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-6 border-t ${dynastyTheme.classes.border.neutral} flex justify-end`}>
          <button
            onClick={onClose}
            className={dynastyTheme.components.button.secondary}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionLog;